import Slider, { type SliderProps as DefaultSliderProps } from '@mui/material/Slider'

import { type FC, useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'

import useWindowSize from 'hooks/useWindowSize'
import media from 'styles/media'
import ValueLabel from './ValueLabel'

export type SliderProps = Omit<DefaultSliderProps, 'onChange'> & {
	minValue: number
	maxValue: number
	steps?: number
	onChange: (event: Event, value: number | number[]) => void
	className?: string
	showMarkLabels?: boolean
}

const SliderComponent: FC<SliderProps> = ({
	minValue,
	maxValue,
	defaultValue,
	steps,
	onChange,
	...props
}) => {
	const { deviceType } = useWindowSize()
	const isMobile = useMemo(() => deviceType === 'mobile', [deviceType])

	const handleOnChange = useCallback(
		(event: Event, value: number | number[]) => onChange(event, value),
		[onChange]
	)

	return (
		<SliderContainer>
			<SliderEnd side="left" isMobile={isMobile} />
			<StyledSlider
				min={minValue}
				max={maxValue}
				step={steps}
				marks={false}
				valueLabelDisplay="auto"
				defaultValue={defaultValue ?? minValue}
				slots={{
					valueLabel: ValueLabel,
				}}
				onChange={handleOnChange}
				{...props}
			/>
			<SliderEnd side="right" isMobile={isMobile} />
		</SliderContainer>
	)
}

export default SliderComponent

export const getStep = (maxValue: number) => {
	if (maxValue < 0.01) return 0.0001
	if (maxValue < 0.1) return 0.001
	if (maxValue < 10) return 0.01
	if (maxValue < 100) return 1
	if (maxValue < 10000) return 10
	return 10 ** (Math.floor(Math.log10(maxValue)) - 3)
}

const styledMarkLabel = css`
	font-weight: 400;
	font-feature-settings: 'tnum';
	font-size: 13px;
	color: ${(props) => props.theme.colors.selectedTheme.slider.label};
	${media.lessThan('sm')`
		top: 30px;
	`}
`

const SliderContainer = styled.div`
	width: 100%;
	height: 24px;
	padding: 0px 4px 0px 4px;
	box-sizing: border-box;
	position: relative;
`

const StyledSlider = styled(Slider)<{ showMarkLabels?: boolean }>`
	color: transparent !important;

	.MuiSlider-markLabel {
		display: ${(props) => !props.showMarkLabels && 'none'};
	}
	
	.MuiSlider-root {
		padding: 10px 0px 10px 4px;
	}

	.MuiSlider-rail {
		z-index: 10;
		// width: 102%;
		margin-top: -2px;
		border-radius: 0px;
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.rail.background};
		height: 4px;
		left: 0;
		right: 0;
		opacity: 1;
	}

	.MuiSlider-track {
		z-index: 10;
		height: 4px;
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.track.background};
		margin-top: -2px;
		border-top-left-radius: 0px;
		border-bottom-left-radius: 0px;
		border-top-right-radius: 0px;
		border-bottom-right-radius: 0px;
	}

	.MuiSlider-mark {
		display: ${(props) => !props.marks && 'none'};
		width: 1px;
		height: 10px;
		color: ${(props) => props.theme.colors.selectedTheme.slider.track.background};
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.track.background};
		opacity: 1;
		top: auto;
	}

	.MuiSlider-markActive {
		display: ${(props) => !props.marks && 'none'};
		top: auto;
		width: 1px;
		height: 10px;
		color: ${(props) => props.theme.colors.selectedTheme.slider.track.background};
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.track.background};
		opacity: 1;
	}

	.MuiSlider-thumb {
		z-index: 50;
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.thumb.background};
		border: ${(props) => props.theme.colors.selectedTheme.slider.thumb.border};
		width: 15px;
		height: 15px;
		margin-left: 2px;
		margin-top: -2px;
		&.Mui-disabled {
			background-color:  ${(props) => props.theme.colors.selectedTheme.slider.rail.background};
			border: ${(props) => props.theme.colors.selectedTheme.slider.thumb.disabledBorder};
		}
	}

	.MuiSlider-markLabelActive {
		${styledMarkLabel}
		margin-left: 6px;
	}

	.MuiSlider-markLabel[data-index='1'] {
		${styledMarkLabel}
		margin-left: -3px;
	}

	.MuiSlider-markLabel:nth-child(7) {
		color: #787878 !important;
	}
`

const SliderEnd = styled.div<{ side: 'left' | 'right'; isMobile: boolean }>`
	z-index: 1;
	position: absolute;
	background-color: ${(props) =>
		props.side === 'left'
			? props.theme.colors.selectedTheme.slider.track.background
			: props.theme.colors.selectedTheme.slider.track.background};
	width: 1px;
	height: 10px;
	top: ${({ isMobile }) => (isMobile ? '14.7px' : '8px')};
	${({ side }) => (side === 'left' ? 'left: 3px;' : 'right: 4px;')}
`
